import {useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import {
  type OptimisticUpdateRollbackData,
  updateMemexItemPositionInQueryClient,
} from './query-client-api/memex-items/update-items'
import type {ReorderItemData} from './types'

export interface PreviousItemPosition {
  /**
   * The id of the item directly before the item that was moved.
   * If the item that was moved is now the top, we represent this with an empty string ''
   */
  previousItemId: number | ''
  /**
   * The old index in the list of the item that was just moved.
   */
  previousMovingItemIndex?: number
}

type SetNewRowPositionHookReturnType = {
  /**
   * Optimistically updates the position of an item in the list of items locally, for a project
   * with the MWL FF enabled. Just a thin wrapper around the query-client-api method.
   * @param movingItemId id of the item that is being moved
   * @param reorderData metadata about where we're moving the item to
   * @returns
   */
  setNewRowPosition: (
    movingItemId: number,
    reorderData: ReorderItemData,
  ) => {previousItemId: number | '' | undefined; rollbackData: OptimisticUpdateRollbackData} | void
}

export const useSetNewRowPosition = (): SetNewRowPositionHookReturnType => {
  const queryClient = useQueryClient()

  const setNewRowPosition = useCallback(
    (movingItemId: number, reorderData: ReorderItemData) => {
      return updateMemexItemPositionInQueryClient(queryClient, movingItemId, reorderData)
    },
    [queryClient],
  )

  return {setNewRowPosition}
}
